import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Clock, Check, LogOut, CreditCard, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TIERS = [
  {
    value: 'basic',
    name: 'Basic',
    description: '2 operators, 1 user included',
    features: ['Core Scheduling', 'Job Management', 'Docket Generation', 'Mobile Operator App'],
  },
  {
    value: 'advanced',
    name: 'Advanced',
    description: '2 operators, 1 user included',
    features: ['Everything in Basic', 'Reports & Analytics', 'Forms & Compliance', 'Timesheets', 'Quotes'],
  },
  {
    value: 'professional',
    name: 'Professional',
    description: '2 operators, 1 user included',
    features: ['Everything in Advanced', 'AI Features & OCR', 'API Access', 'Priority Support'],
  },
];

export default function SubscriptionRequired() {
  const { api, user, logout, subscriptionStatus, gracePeriodExpiresAt } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [loading, setLoading] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    if (gracePeriodExpiresAt) {
      const expires = new Date(gracePeriodExpiresAt);
      const now = new Date();
      const diff = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
      setDaysRemaining(diff);
    }
  }, [gracePeriodExpiresAt]);

  const isSuspended = subscriptionStatus === 'suspended';
  const isCancelled = subscriptionStatus === 'cancelled';

  const handleSelectTier = async (tier) => {
    setLoading(tier);
    try {
      const res = await api.post('/billing/checkout/subscription', {
        tier,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(detail);
    } finally {
      setLoading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    logout();
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${isDark ? 'bg-[#0a0a1a]' : 'bg-gray-50'}`}>
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            {isCancelled ? (
              <AlertTriangle className="h-10 w-10 text-red-500" />
            ) : isSuspended ? (
              <AlertTriangle className="h-10 w-10 text-red-400" />
            ) : (
              <Clock className="h-10 w-10 text-[#ffd108]" />
            )}
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {isCancelled ? 'Account Cancelled' : isSuspended ? 'Account Suspended' : 'Subscription Required'}
          </h1>
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            {isCancelled
              ? 'This account has been cancelled and is no longer active. Please contact support if you believe this is an error.'
              : isSuspended
              ? 'Your account has been suspended. Please select a subscription plan to restore access.'
              : `You have ${daysRemaining ?? '?'} day${daysRemaining !== 1 ? 's' : ''} remaining to select a plan.`}
          </p>
          {user?.company_name && (
            <Badge className="mt-3 bg-[#ffd108]/20 text-[#ffd108] border-[#ffd108]/30">
              {user.company_name}
            </Badge>
          )}
        </div>

        {/* Tier Cards — only show for suspended / grace period, not cancelled */}
        {!isCancelled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="subscription-tier-cards">
          {TIERS.map((tier) => (
            <Card
              key={tier.value}
              className={`relative overflow-hidden transition-all hover:scale-[1.02] ${
                isDark
                  ? 'bg-slate-800/60 border-slate-700 hover:border-[#ffd108]/50'
                  : 'bg-white border-gray-200 hover:border-[#ffd108] shadow-sm'
              } ${tier.value === 'advanced' ? 'ring-2 ring-[#ffd108]/60' : ''}`}
            >
              {tier.value === 'advanced' && (
                <div className="absolute top-0 right-0 bg-[#ffd108] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                  POPULAR
                </div>
              )}
              <CardHeader className="pb-3">
                <CardTitle className={`text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {tier.name}
                </CardTitle>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {tier.description}
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                      <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`select-tier-${tier.value}`}
                  className="w-full bg-[#ffd108] hover:bg-[#e6bc00] text-black font-semibold"
                  onClick={() => handleSelectTier(tier.value)}
                  disabled={loading !== null}
                >
                  {loading === tier.value ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Select {tier.name}
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {/* Footer */}
        <div className="text-center flex items-center justify-center gap-4">
          <Button
            data-testid="subscription-support-btn"
            variant="outline"
            onClick={() => navigate('/support')}
            className={isDark ? 'text-[#ffd108] border-[#ffd108]/30 hover:bg-[#ffd108]/10 hover:text-[#ffd108]' : 'text-[#b8960b] border-[#ffd108]/50 hover:bg-[#ffd108]/10'}
          >
            <Ticket className="h-4 w-4 mr-2" />
            Support
          </Button>
          <Button
            data-testid="subscription-logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
}
